import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { GiftCardVariant, Maybe } from '@storefront/lib-global/codegen/types';
import { ascendingAlphaNum } from '@storefront/lib-global/utils/ascendingAlphaNum';

type OccasionAndDenominationContextType = {
  selectedOccasionVariant: GiftCardVariant;
  selectedPriceDenomination?: Maybe<string> | undefined;
  uniqueOccasions: GiftCardVariant[];
  uniquePriceDenominations: GiftCardVariant[];
  isSingleOccasion: boolean;
  handleChangeOccasion(variant: GiftCardVariant): void;
  handleChangePriceDenomination(priceDenomination: Maybe<string> | undefined, occasion: string): void;
  selectedGiftCardSku?: string;
  priceDenominationError: boolean;
  setPriceDenominationError: Dispatch<SetStateAction<boolean>>;
};

type OccasionAndDenominationContextProviderType = {
  children: ReactNode;
  giftCardVariants: GiftCardVariant[];
};

const OccasionAndDenominationContext = createContext<OccasionAndDenominationContextType>({
  selectedOccasionVariant: {} as GiftCardVariant,
  selectedPriceDenomination: '',
  uniqueOccasions: [],
  uniquePriceDenominations: [],
  isSingleOccasion: false,
  handleChangeOccasion: () => {},
  handleChangePriceDenomination: () => {},
  selectedGiftCardSku: '',
  priceDenominationError: false,
  setPriceDenominationError: () => {},
});

export const useOccasionAndDenominationContext = () => useContext(OccasionAndDenominationContext);

export function OccasionAndDenominationProvider({
  children,
  giftCardVariants,
}: OccasionAndDenominationContextProviderType) {
  const [selectedOccasionVariant, setSelectedVariant] = useState<GiftCardVariant>(giftCardVariants[0] ?? {});
  const selectedOccasion = selectedOccasionVariant.occasion as string;
  const [selectedGiftCardSku, setSelectedGiftCardSku] = useState<string | undefined>('');
  const [priceDenominationError, setPriceDenominationError] = useState(false);

  const uniquePriceDenominations = useMemo(
    () =>
      giftCardVariants
        .reduce((priceDenominations, variant) => {
          if (!(variant && variant.occasion && variant.priceDenomination)) return priceDenominations;
          if (variant.occasion !== selectedOccasion) return priceDenominations;

          const isDuplicate = priceDenominations.some((el) => el.priceDenomination === variant.priceDenomination);
          if (isDuplicate) return priceDenominations;

          priceDenominations.push(variant);
          return priceDenominations;
        }, [] as GiftCardVariant[])
        .sort((a, b) => ascendingAlphaNum(a.priceDenomination as string, b.priceDenomination as string)),
    [giftCardVariants, selectedOccasion],
  );

  const [priceDenominationMap, setPriceDenominationMap] = useState<Record<string, Maybe<string> | undefined>>({
    [selectedOccasion]:
      uniquePriceDenominations.length === 1 ? uniquePriceDenominations[0].priceDenomination : undefined,
  });

  const selectedPriceDenomination = priceDenominationMap[selectedOccasion];
  const hasUserSelectedPD = useRef(false);

  const uniqueOccasions = useMemo(
    () =>
      giftCardVariants.reduce((occasionVariants, variant) => {
        if (!(variant && variant.occasion)) return occasionVariants;
        if (
          selectedPriceDenomination &&
          selectedPriceDenomination !== variant.priceDenomination &&
          hasUserSelectedPD.current
        )
          return occasionVariants;

        const isDuplicate = occasionVariants.some((el) => el.occasion === variant.occasion);
        if (isDuplicate) return occasionVariants;

        occasionVariants.push(variant);
        return occasionVariants;
      }, [] as GiftCardVariant[]),
    [giftCardVariants, selectedPriceDenomination],
  );

  const handleChangeOccasion = useCallback((variant: GiftCardVariant) => setSelectedVariant(variant), []);

  const handleChangePriceDenomination = useCallback(
    (priceDenomination: Maybe<string> | undefined, occasion: string) => {
      if (!hasUserSelectedPD.current) hasUserSelectedPD.current = true;
      setPriceDenominationMap((prev) => ({ ...prev, [occasion]: priceDenomination }));
      if (priceDenominationError) setPriceDenominationError(false);
    },
    [priceDenominationError],
  );

  useEffect(() => {
    setSelectedVariant(giftCardVariants[0] ?? {});
  }, [giftCardVariants]);

  useEffect(() => {
    const isSingleAndDifferentDenomination =
      uniquePriceDenominations.length === 1 &&
      selectedPriceDenomination !== uniquePriceDenominations[0].priceDenomination;

    if (isSingleAndDifferentDenomination) {
      setPriceDenominationMap((prev) => ({
        ...prev,
        [selectedOccasion]: uniquePriceDenominations[0].priceDenomination,
      }));
    }
  }, [uniquePriceDenominations, selectedOccasion, selectedPriceDenomination]);

  const isSingleOccasion = useMemo(
    () => giftCardVariants.every((variant) => variant.occasion === giftCardVariants[0]?.occasion),
    [giftCardVariants],
  );

  useEffect(() => {
    setSelectedGiftCardSku(
      giftCardVariants
        ?.filter((x) => x.occasion === selectedOccasionVariant?.occasion)
        .find((y) => y.priceDenomination === selectedPriceDenomination)?.sku,
    );
  }, [giftCardVariants, selectedOccasionVariant?.occasion, selectedPriceDenomination]);

  const occasionAndDenomination = useMemo(
    () => ({
      selectedOccasionVariant,
      selectedPriceDenomination,
      isSingleOccasion,
      uniqueOccasions,
      uniquePriceDenominations,
      handleChangeOccasion,
      handleChangePriceDenomination,
      selectedGiftCardSku,
      priceDenominationError,
      setPriceDenominationError,
    }),
    [
      selectedOccasionVariant,
      selectedPriceDenomination,
      isSingleOccasion,
      uniqueOccasions,
      uniquePriceDenominations,
      handleChangeOccasion,
      handleChangePriceDenomination,
      selectedGiftCardSku,
      priceDenominationError,
      setPriceDenominationError,
    ],
  );

  return (
    <OccasionAndDenominationContext.Provider value={occasionAndDenomination}>
      {children}
    </OccasionAndDenominationContext.Provider>
  );
}
